import React, { useEffect, useRef, useState } from "react";

const WpDynamicTable = ({
  columns = [],
  rows = [],
  loading = false,
  height = "400px",
  rowsPerPage = 10,
  hideSearch = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);

  // Update width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  // Filter data BEFORE paginating
  const filteredRows = rows.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;
  const currentItems = filteredRows.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Reset page when searchTerm changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="w-full overflow-hidden">
      {/* Search Input */}
      {!hideSearch && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search..."
            className="border-2 border-black px-3 py-2 rounded w-full max-w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* Scrollable Table Wrapper */}
      <div
        ref={containerRef}
        className="w-full overflow-x-auto bg-white"
        style={{ width: "100%" }}
      >
        {/* Table Container */}
        <div
          className="min-w-full border border-[#122A47] rounded-xl overflow-hidden"
          style={{ width: containerWidth }}
        >
          <div
            className="overflow-y-auto scrollbar-custom"
            style={{ height }}
          >
            <table className="min-w-full table-auto overflow-x-auto border-collapse text-sm">
              {/* Table Head */}
              <thead className="bg-[#cfe2ff] sticky top-0 z-20">
                <tr>
                  {columns.map((col, idx) => (
                    <th
                      key={idx}
                      className="px-3 py-3 text-center whitespace-nowrap text-gray-800 border border-[#122A47]"
                      style={{ width: col.minWidth || "120px" }}
                    >
                      {col.headerName}
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-4 text-gray-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : currentItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-4 text-red-500 font-semibold"
                    >
                      No data available.
                    </td>
                  </tr>
                ) : (
                  currentItems.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="hover:bg-gray-50 text-center align-middle"
                    >
                      {columns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          className={`px-3 py-2 border ${col.wrap
                            ? "break-words whitespace-normal text-center"
                            : "whitespace-nowrap"
                            }`}
                          style={{
                            maxWidth: col.maxWidth || (col.wrap ? "150px" : "auto"),
                            minWidth: col.minWidth || "auto",
                          }}
                        >
                          {col.renderCell
                            ? col.renderCell({ row })
                            : typeof row[col.field] === "object" && row[col.field] !== null
                              ? JSON.stringify(row[col.field])
                              : row[col.field] ?? "-"}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex justify-center sm:justify-end mt-4">
        <nav
          className="flex items-center space-x-1 text-sm"
          aria-label="Page navigation"
        >
          {/* Previous */}
          <button
            className={`px-3 py-2 Prev_button rounded border ${currentPage === 1 || loading
              ? "opacity-50 cursor-not-allowed"
              : "Prev_button"
              }`}
            onClick={() => !loading && handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>

          {/* First */}
          {currentPage > 2 && (
            <button
              className="px-3 py-1 rounded border bg-gray-300 hover:bg-gray-400"
              onClick={() => handlePageChange(1)}
            >
              1
            </button>
          )}

          {/* Current Page */}
          <span className="px-3 py-1 rounded border count_button text-white font-semibold">
            {currentPage}
          </span>

          {/* Last */}
          {currentPage < totalPages && (
            <button
              className="px-3 py-1 rounded border bg-gray-300 hover:bg-gray-400"
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          )}

          {/* Next */}
          <button
            className={`px-3 py-2 Next_button rounded border ${currentPage === totalPages || loading
              ? "opacity-50 cursor-not-allowed"
              : "Next_button"
              }`}
            onClick={() => !loading && handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            Next
          </button>
        </nav>
      </div>
    </div>
  );
};

export default WpDynamicTable;
